import React, { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { API_BASE_URL, authFetch } from '../config/api';

interface Props {
  /** Vault-relative path of the note being targeted (e.g. "2 Areas/Food Safety/Hazards/Listeria.md"). */
  targetPath: string;
  /** Optional dimhazards_id hint, usually pulled from the note's frontmatter. */
  hazardId?: number;
  /** Optional display label for the hazard (shown in the modal header). */
  hazardLabel?: string;
  /** Called when the user dismisses the modal. */
  onClose: () => void;
}

interface IngestResponse {
  ok: boolean;
  job_id: number;
  source: string;
  targeted: boolean;
}

async function ingestFile(
  file: File,
  targetPath: string,
  hazardId?: number,
): Promise<IngestResponse> {
  const form = new FormData();
  form.append('file', file);
  form.append('target_file_path', targetPath);
  if (hazardId) form.append('hazard_id', String(hazardId));
  const res = await authFetch(`${API_BASE_URL}/api/knowledge/ingest`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

async function ingestUrl(
  url: string,
  targetPath: string,
  hazardId?: number,
): Promise<IngestResponse> {
  const res = await authFetch(`${API_BASE_URL}/api/knowledge/ingest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url,
      target_file_path: targetPath,
      ...(hazardId ? { hazard_id: hazardId } : {}),
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Targeted ingestion modal — reuses the existing ingestion pipeline but
 * pre-wires the resulting proposal to the specific note the user is
 * currently viewing. The worker short-circuits the related-notes scan
 * and merges the new content into this note.
 */
export default function IngestToNoteModal({ targetPath, hazardId, hazardLabel, onClose }: Props) {
  const [mode, setMode] = useState<'file' | 'url'>('file');
  const [urlInput, setUrlInput] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<IngestResponse | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const fileMut = useMutation(
    (file: File) => ingestFile(file, targetPath, hazardId),
    { onSuccess: (data) => setResult(data) },
  );

  const urlMut = useMutation(
    (url: string) => ingestUrl(url, targetPath, hazardId),
    { onSuccess: (data) => { setResult(data); setUrlInput(''); } },
  );

  const isLoading = fileMut.isLoading || urlMut.isLoading;
  const error = fileMut.error || urlMut.error;

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) fileMut.mutate(file);
  }
  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) fileMut.mutate(file);
  }

  const filename = targetPath.split('/').pop() ?? targetPath;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal ingest-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal__header">
          <div>
            <h3 className="modal__title">Ingest document</h3>
            <div className="modal__subtitle">
              <span className="ingest-modal__tag" aria-hidden>{'\u2192'}</span>
              <span className="ingest-modal__target">{filename}</span>
              {hazardLabel && (
                <span className="ingest-modal__hazard">
                  hazard: <strong>{hazardLabel}</strong>
                </span>
              )}
            </div>
          </div>
          <button
            className="modal__close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            {'\u2715'}
          </button>
        </header>

        <div className="modal__body">
          {result ? (
            <div className="ingest-modal__success">
              <div className="ingest-modal__success-icon" aria-hidden>{'\u2713'}</div>
              <div className="ingest-modal__success-text">
                <strong>Job #{result.job_id} created.</strong>
                <span>
                  The worker will process <code>{result.source}</code> and stage a
                  proposal targeting <code>{filename}</code>.
                </span>
                <span className="text-muted">
                  Review proposals on the <a href="/knowledge/staging">Knowledge staging page</a>.
                </span>
              </div>
            </div>
          ) : (
            <>
              <div className="ingest-modal__tabs" role="tablist">
                <button
                  role="tab"
                  aria-selected={mode === 'file'}
                  className={`ingest-modal__tab${mode === 'file' ? ' ingest-modal__tab--active' : ''}`}
                  onClick={() => setMode('file')}
                  type="button"
                >
                  Upload file
                </button>
                <button
                  role="tab"
                  aria-selected={mode === 'url'}
                  className={`ingest-modal__tab${mode === 'url' ? ' ingest-modal__tab--active' : ''}`}
                  onClick={() => setMode('url')}
                  type="button"
                >
                  From URL
                </button>
              </div>

              {mode === 'file' ? (
                <div
                  className={`ingest-modal__drop${dragOver ? ' ingest-modal__drop--active' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current?.click()}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.docx,.doc,.txt"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  {isLoading ? (
                    <span>Uploading...</span>
                  ) : (
                    <>
                      <span className="ingest-modal__drop-icon" aria-hidden>{'\u{1F4E4}'}</span>
                      <span className="ingest-modal__drop-text">
                        Drop a file or click to browse
                      </span>
                      <span className="ingest-modal__drop-hint">
                        PDF · DOCX · TXT (max 50&nbsp;MB)
                      </span>
                    </>
                  )}
                </div>
              ) : (
                <form
                  className="ingest-modal__url-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (urlInput) urlMut.mutate(urlInput);
                  }}
                >
                  <input
                    type="url"
                    className="ingest-modal__url-input"
                    placeholder="https://www.efsa.europa.eu/..."
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    disabled={isLoading}
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="btn btn--approve"
                    disabled={isLoading || !urlInput}
                  >
                    {isLoading ? 'Submitting...' : 'Ingest'}
                  </button>
                </form>
              )}

              {error && (
                <div className="ingest-modal__error">
                  Error: {(error as Error).message}
                </div>
              )}

              <p className="ingest-modal__note text-muted">
                The document will run through the full extraction &rarr; classification
                pipeline. The resulting proposal is pre-routed to this note — review
                and approve it from the staging page.
              </p>
            </>
          )}
        </div>

        <footer className="modal__footer">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
          >
            {result ? 'Close' : 'Cancel'}
          </button>
        </footer>
      </div>
    </div>
  );
}
